import { useMemo } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { type ColumnDef } from "@tanstack/react-table"
import { Users } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { EntityTablePage } from "@/features/shared/components/entity-table-page"
import { useSiteUsers, useSites } from "@/lib/hooks/use-sites"
import type { UserRole } from "@/types/api"

export const Route = createFileRoute("/_authenticated/users")({
  component: UsersPage,
})

function getRoleVariant(role: string) {
  switch (role) {
    case "Administrator":
      return "destructive"
    case "Editor":
      return "warning"
    default:
      return "secondary"
  }
}

function getColumns(siteNameMap: Map<string, string>): ColumnDef<UserRole, unknown>[] {
  return [
    {
      accessorKey: "site_slug",
      header: "Site",
      cell: ({ row }) =>
        siteNameMap.get(row.original.site_slug) ?? row.original.site_slug,
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => {
        const name = `${row.original.first_name} ${row.original.last_name}`.trim()
        return name || "—"
      },
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "site_role",
      header: "Role",
      cell: ({ row }) => (
        <Badge variant={getRoleVariant(row.original.site_role)}>
          {row.original.site_role}
        </Badge>
      ),
    },
  ]
}

function UsersPage() {
  const { data: siteUsers, isLoading, isError, error } = useSiteUsers()
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  const columns = useMemo(() => getColumns(siteNameMap), [siteNameMap])

  return (
    <EntityTablePage
      icon={<Users className="h-5 w-5 text-[var(--color-accent-blue)]" />}
      title="Users"
      description={`${siteUsers?.length ?? 0} users across all sites`}
      searchPlaceholder="Search users..."
      emptyMessage="No users found"
      columns={columns}
      data={siteUsers}
      isLoading={isLoading}
      isError={isError}
      error={error}
    />
  )
}
